import { useCallback, useEffect, useState } from 'react';
import { useLoadingState } from '../hooks';
import { TwindenLoader } from '../components/brand/TwindenLoader';
import { useNavigate, useParams } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { markGettingKeys } from '../components/chat/GettingKeysNotice';
import {
  Alert,
  AlertIcon,
  Button,
  Center,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { KeyRound } from 'lucide-react';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useAuth } from '../hooks/useAuth';
import { api, ApiError } from '../lib/api';
import { WelcomeShell } from '../components/page/WelcomeShell';
import { JoinInTheApp } from '../components/site/StoreBadges';
import { guessDeviceName } from '../lib/devices';
import { currentFamilyIdAtom } from '../store';
import { ContinueAs, JoinChoice } from '../components/family/JoinChoice';
import {
  HandlePicker,
  type HandleValue,
} from '../components/family/HandlePicker';
import { signInErrorMessage } from './LoginPage';
import { t as translateNow, useT } from '../i18n';

type PageState =
  | { status: 'loading' }
  | { status: 'unsupported' }
  | { status: 'error'; message: string }
  | {
      status: 'ready';
      kind: 'enroll' | 'add_device';
      familyId: string;
      familyName: string;
      familyHandle: string | null;
      handle: string | null;
      displayName: string | null;
    }
  | { status: 'registering'; handle: string | null };

// Also used by the family link's page (JoinLinkPage).
export function joinErrorMessage(err: unknown): string {
  // Always log the raw error — the browser console shows the real cause
  console.error('[enroll] ceremony error:', err);
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'TOKEN_NOT_FOUND':
        return translateNow('account.join.tokenNotFound');
      case 'TOKEN_CONSUMED':
        return translateNow('account.join.tokenConsumed');
      case 'TOKEN_REVOKED':
        return translateNow('account.join.tokenRevoked');
      case 'TOKEN_EXPIRED':
        return translateNow('account.join.tokenExpired');
      case 'ADD_DEVICE_SELF_ONLY':
        // A device link someone else made before D334 (#105).
        return translateNow('account.join.addDeviceSelfOnly');
      case 'HANDLE_TAKEN':
        return translateNow('account.join.handleTaken');
      case 'DEVICE_ALREADY_REGISTERED':
        return translateNow('account.join.alreadyRegistered');
      case 'CHALLENGE_NOT_FOUND':
        return translateNow('account.join.sessionExpired');
      case 'VERIFICATION_FAILED':
        return translateNow('account.login.verificationFailed');
      default:
        return err.message;
    }
  }
  // User dismissed the authenticator prompt
  if (err instanceof Error && err.name === 'NotAllowedError') {
    return translateNow('account.login.cancelled');
  }
  // RP ID / origin mismatch — page opened on a host other than RP_ID
  if (err instanceof Error && err.name === 'SecurityError') {
    return translateNow('account.join.wrongOrigin');
  }
  if (err instanceof Error && err.name === 'InvalidStateError') {
    return translateNow('account.join.alreadyHasPasskey');
  }
  if (err instanceof Error) {
    return translateNow('account.join.errorDetail', {
      detail: `${err.name}: ${err.message}`,
    });
  }
  return translateNow('account.login.tryAgain');
}

// Who is opening a personal link (D151, §1a): a new person makes an
// account; someone already on Twinden signs in and joins with theirs.
type Who = 'choose' | 'confirm' | 'new' | 'existing';

export function JoinFamilyPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    currentUser,
    isAuthenticated,
    logout,
    signInWithPasskey,
    enrollWithPasskey,
  } = useAuth();
  const setCurrentFamilyId = useSetAtom(currentFamilyIdAtom);
  const t = useT();

  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [deviceName, setDeviceName] = useState(guessDeviceName());
  const [chosenWho, setWho] = useState<Who>(
    isAuthenticated ? 'confirm' : 'choose'
  );
  // Signed out meanwhile: ask again.
  const who: Who =
    !currentUser && (chosenWho === 'confirm' || chosenWho === 'existing')
      ? 'choose'
      : chosenWho;
  const [busy, setBusy] = useState(false);
  // The mark in the shell while the server answers (M66, D248).
  const waiting = useLoadingState({
    loading: state.status === 'loading' || state.status === 'registering',
  });

  useEffect(() => {
    if (!browserSupportsWebAuthn()) {
      setState({ status: 'unsupported' });
      return;
    }
    if (!token) {
      setState({
        status: 'error',
        message: translateNow('account.join.invalid'),
      });
      return;
    }

    api
      .getEnrollmentInfo(token)
      .then((res) => {
        setState({
          status: 'ready',
          kind: res.data.kind,
          familyId: res.data.familyId,
          familyName: res.data.familyName,
          familyHandle: res.data.familyHandle ?? null,
          handle: res.data.handle,
          displayName: res.data.displayName,
        });
      })
      .catch((err) => {
        setState({ status: 'error', message: joinErrorMessage(err) });
      });
  }, [token]);

  // A personal link that holds no handle (D348): the new person picks one
  // here before the passkey, as on the family link (D153).
  const [picked, setPicked] = useState<HandleValue | null>(null);
  const suggest = useCallback(
    async ({ gendered }: { gendered: boolean }) =>
      token
        ? (await api.getEnrollmentSuggestions(token, gendered)).data.suggestions
        : [],
    [token]
  );

  const handleRegister = async () => {
    if (state.status !== 'ready' || !token) return;
    const picksHandle = state.kind === 'enroll' && state.handle === null;
    if (picksHandle && !picked?.token) return;
    const { handle } = state;
    setState({
      status: 'registering',
      handle: handle ?? picked?.handle ?? null,
    });
    try {
      await enrollWithPasskey(
        token,
        deviceName || undefined,
        picksHandle ? (picked ?? undefined) : undefined
      );
      // Your other device is handing over its keys (D138).
      if (state.kind === 'add_device') markGettingKeys();
      navigate('/', { replace: true });
    } catch (err) {
      // Taken meanwhile: offer the server's replacement; lapsed: a new one
      // (D147).
      if (err instanceof ApiError && err.code === 'HANDLE_TAKEN') {
        setPicked(err.suggestion ?? null);
      } else if (
        err instanceof ApiError &&
        err.code === 'HANDLE_NOT_SUGGESTED'
      ) {
        setPicked(null);
      }
      // Back to ready with the error shown, so the user can retry
      setState({
        status: 'ready',
        kind: state.kind,
        familyId: state.familyId,
        familyName: state.familyName,
        familyHandle: state.familyHandle,
        handle: state.handle,
        displayName: state.displayName,
      });
      setInlineError(joinErrorMessage(err));
    }
  };

  const [inlineError, setInlineError] = useState<string | null>(null);

  const signIn = async () => {
    setInlineError(null);
    setBusy(true);
    try {
      await signInWithPasskey();
      setWho('existing');
    } catch (err) {
      setInlineError(signInErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    await logout().catch(() => undefined);
    setInlineError(null);
    setWho('choose');
  };

  // The link is the approval: they're in straight away (D151).
  const join = async () => {
    if (state.status !== 'ready' || !token) return;
    setInlineError(null);
    setBusy(true);
    try {
      const res = await api.joinWithPersonalLink(token);
      setCurrentFamilyId(res.data.familyId);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ALREADY_MEMBER') {
        await openFamily(state.familyId, state.familyName);
        return;
      }
      if (
        err instanceof ApiError &&
        (err.code === 'TOKEN_CONSUMED' ||
          err.code === 'TOKEN_EXPIRED' ||
          err.code === 'TOKEN_REVOKED' ||
          err.code === 'TOKEN_NOT_FOUND')
      ) {
        setState({ status: 'error', message: joinErrorMessage(err) });
        return;
      }
      setInlineError(joinErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // "You're already in this family": open it.
  // By id: two families may share a name (D217).
  const openFamily = async (familyId: string, familyName: string) => {
    try {
      const families = (await api.getFamilies()).data;
      const family = families.find((f) => f.id === familyId);
      if (family) setCurrentFamilyId(family.id);
    } catch {
      // Home picks a family anyway.
    }
    toast({
      status: 'info',
      title: translateNow('account.join.alreadyIn', { family: familyName }),
    });
    navigate('/', { replace: true });
  };

  if (state.status === 'loading') {
    return (
      <WelcomeShell title={t('account.join.joining')}>
        <Center>{waiting.loader && <TwindenLoader />}</Center>
      </WelcomeShell>
    );
  }

  if (state.status === 'unsupported') {
    return (
      <WelcomeShell
        title={t('account.join.unsupportedTitle')}
        subtitle={t('account.join.unsupportedBody')}
      >
        {null}
      </WelcomeShell>
    );
  }

  if (state.status === 'error') {
    return (
      <WelcomeShell title={t('account.join.errorTitle')}>
        <Alert status="warning" borderRadius="xl">
          <AlertIcon />
          {state.message}
        </Alert>
      </WelcomeShell>
    );
  }

  if (state.status === 'registering') {
    return (
      <WelcomeShell
        title={t('account.join.almostTitle')}
        subtitle={t('account.join.almostBody')}
      >
        <Center>{waiting.loader && <TwindenLoader />}</Center>
      </WelcomeShell>
    );
  }

  // ready
  const isAddDevice = state.kind === 'add_device';
  const picksHandle = !isAddDevice && state.handle === null;
  const errorAlert = inlineError && (
    <Alert status="error" borderRadius="xl">
      <AlertIcon />
      {inlineError}
    </Alert>
  );

  if (!isAddDevice && who === 'choose') {
    return (
      <WelcomeShell
        title={t('account.join.linkTitle', { family: state.familyName })}
        handle={state.familyHandle}
        footer={t('account.join.chooseFooter')}
      >
        <JoinChoice
          onNew={() => {
            setInlineError(null);
            setWho('new');
          }}
          onSignIn={signIn}
          signingIn={busy}
          error={inlineError}
        />
        {/* On a phone, the app instead (D346): this link opens in it. */}
        <JoinInTheApp />
      </WelcomeShell>
    );
  }

  if (!isAddDevice && who === 'confirm' && currentUser) {
    return (
      <WelcomeShell
        title={t('account.join.linkTitle', { family: state.familyName })}
        handle={state.familyHandle}
      >
        <ContinueAs
          name={currentUser.displayName}
          onContinue={() => setWho('existing')}
          onSignOut={signOut}
        />
      </WelcomeShell>
    );
  }

  if (!isAddDevice && who === 'existing' && currentUser) {
    return (
      <WelcomeShell
        title={t('account.join.personalTitle', { family: state.familyName })}
        handle={state.familyHandle}
        subtitle={t('account.join.personalBody', {
          handle: currentUser.handle,
        })}
      >
        <VStack spacing={4} align="stretch">
          {errorAlert}
          <Button h="56px" fontSize="17px" onClick={join} isLoading={busy}>
            {t('account.join.join')}
          </Button>
        </VStack>
      </WelcomeShell>
    );
  }

  return (
    <WelcomeShell
      title={
        isAddDevice
          ? t('account.join.addDevice')
          : t('account.join.welcome', { name: state.displayName ?? '' })
      }
      subtitle={
        isAddDevice
          ? t('account.join.addDeviceBody', {
              handle: state.handle ?? '',
              family: state.familyName,
            })
          : picksHandle
            ? t('account.join.joiningBodyPick', { family: state.familyName })
            : t('account.join.joiningBody', {
                handle: state.handle ?? '',
                family: state.familyName,
              })
      }
      footer={
        isAddDevice
          ? t('account.join.addDeviceFooter')
          : t('account.join.joiningFooter')
      }
    >
      <VStack spacing={4} align="stretch">
        {errorAlert}
        {picksHandle && (
          <HandlePicker
            label={t('account.join.youllBe')}
            suggest={suggest}
            value={picked}
            onChange={setPicked}
          />
        )}
        <FormControl>
          <FormLabel>{t('account.join.nameDevice')}</FormLabel>
          <Input
            h="52px"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            placeholder={t('account.join.nameDevicePlaceholder')}
            maxLength={100}
          />
        </FormControl>
        <Button
          h="56px"
          fontSize="17px"
          leftIcon={<KeyRound size={20} />}
          onClick={handleRegister}
          isDisabled={picksHandle && !picked?.token}
        >
          {isAddDevice
            ? t('account.join.addDevice')
            : t('account.join.createPasskey')}
        </Button>
      </VStack>
    </WelcomeShell>
  );
}
